<template>
  <form @submit="onSubmit">
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField name="code" :label="t('sourceentity.detail.code')" :placeholder="t('sourceentity.detail.code')" />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="label"
        :label="t('sourceentity.detail.label')"
        :placeholder="t('sourceentity.detail.label')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsTextAreaField
        name="description"
        :label="t('sourceentity.detail.description')"
        :placeholder="t('sourceentity.detail.description')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="model"
        :label="t('sourceentity.detail.model')"
        :placeholder="$t('sourceentity.detail.model')"
        :options="entityModels"
        :custom-label="entityModelLabel"
        :sortFunction="entityModelSortFunction"
        :mod-loading="fetchingModels"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="crs"
        :label="t('sourceentity.detail.crs')"
        :placeholder="$t('sourceentity.detail.crs')"
        :options="sridTypes"
        :custom-label="(e) => e.name"
        :mod-loading="fetchingModels"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="dataConnection"
        :label="t('sourceentity.detail.dataConnection')"
        :placeholder="$t('sourceentity.detail.dataConnection')"
        :mod-loading="dataConnectionStore.loading"
        :options="dataConnections"
        :custom-label="(e) => e.code + ' (' + e.label + ')'"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="schema"
        :label="t('sourceentity.detail.schema')"
        :placeholder="t('sourceentity.detail.schema')"
      />
    </vl-form-column>
    <vl-action-group v-vl-spacer:bottom.small>
      <vl-button icon="save" :mod-loading="reloading" mod-icon-before type="submit">
        {{ $t('general.save') }}
      </vl-button>
      <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeCreate">
        {{ $t('general.cancel') }}
      </vl-button>
    </vl-action-group>
  </form>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { DataConnectionDto, EnumerationDto, SearchEntityModelDto } from '@/api/backoffice/clients';
import { useForm } from 'vee-validate';
import { object, string, mixed } from 'yup';
import { useI18n } from 'vue-i18n';
import { useSourceEntityStore } from '@/modules/configuration/store/source-entity/source-entity.store';
import { useEntityModelSelectStore } from '@/modules/configuration/store/entity-model/entity-model-select.store';
import { ENUM_SridTypes } from '@/modules/configuration/common';
import { useProductProcessDefinitionEditStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-edit.store';
import { useDataconnectionStore } from '@/modules/configuration/store/dataconnection/dataconnection.store';
import { useProductProcessDefinitionDetailStore } from '@/modules/configuration/store/product-process-definition/product-process-definition-detail.store';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsInputField from '@/components/forms/PbsInputField.vue';
import PbsTextAreaField from '@/components/forms/PbsTextAreaField.vue';
import { entityModelLabel, entityModelSortFunction } from '@/modules/configuration/common';
import { RegularExpressions, noSurroundingSpaces } from '@/helpers';
import { ENUM_EntityTypes } from '@/modules/configuration/common';

const { t } = useI18n();

const store = useSourceEntityStore();
const entityModelSelectStore = useEntityModelSelectStore();
const editStore = useProductProcessDefinitionEditStore();
const dataConnectionStore = useDataconnectionStore();

const props = defineProps({
  specificationId: {
    type: Number,
    required: true
  }
});

const validationSchema = object({
  label: string()
    .required(t('validations.messages.required', { _field_: t('sourceentity.detail.label') }))
    .test(noSurroundingSpaces(t('sourceentity.detail.label')))
    .max(256, t('validations.messages.maxChar', { _field_: t('sourceentity.detail.label'), length: 256 })),
  code: string()
    .required(t('validations.messages.required', { _field_: t('sourceentity.detail.code') }))
    .max(64, t('validations.messages.maxChar', { _field_: t('sourceentity.detail.code'), length: 64 }))
    .matches(
      RegularExpressions.noSpaces,
      t('validations.messages.noSpaces', { _field_: t('sourceentity.detail.code') })
    ),
  description: string().required(t('validations.messages.required', { _field_: t('sourceentity.detail.description') })),
  model: object().required(t('validations.messages.required', { _field_: t('sourceentity.detail.model') })),
  dataConnection: object().required(
    t('validations.messages.required', { _field_: t('sourceentity.detail.dataConnection') })
  ),
  schema: string()
    .required(t('validations.messages.required', { _field_: t('sourceentity.detail.schema') }))
    .matches(
      RegularExpressions.noSpaces,
      t('validations.messages.noSpaces', { _field_: t('sourceentity.detail.schema') })
    ),
  custom: mixed().test(
            'custom-validation',
            'The properties do not meet the validation criteria',
            function (_, context) {
                const { crs
                } = context.parent;
                           
                if (!crs && !isCodeListOrTable.value) {
                  return context.createError({
                    path: 'crs',
                    message: t('validations.messages.required', {
                      _field_: t('sourceentity.detail.crs')
                    })
                  });  
                }

                return true;
            }
        )
});

const { resetForm, handleSubmit, values } = useForm({
  validationSchema
});

const reloading = computed((): boolean => {
  return store.reloading;
});

const sridTypes = computed((): EnumerationDto[] => {
  return Object.values(ENUM_SridTypes);
});

const fetchingModels = computed((): Boolean => {
  return entityModelSelectStore.loading;
});

const entityModels = computed((): SearchEntityModelDto[] => {
  return entityModelSelectStore.items ?? [];
});

const entityModel = computed(() => {
  return values.model;
});

const isCodeListOrTable = computed((): boolean => {
  return entityModel.value === undefined || 
    entityModel.value?.entityTypeName === ENUM_EntityTypes.CodeList.name || entityModel.value?.entityTypeName === ENUM_EntityTypes.Table.name;
});

const dataConnections = computed((): DataConnectionDto[] => {
  return dataConnectionStore.items ?? [];
});

const defaultScheme = computed((): string => {
  return useProductProcessDefinitionDetailStore().detail.deliveryProcessDefinition?.defaultScheme;
});

onMounted(async () => {
  await dataConnectionStore.get();
  await entityModelSelectStore.getModels({ force: true, contextTypeName: 'Source' });
  resetForm({
    values: {
      schema: defaultScheme.value,
      dataConnection: dataConnections.value.find((x) => x.id === 3)
    }
  });
});

const onSuccess = async (values) => {
  await store.create(props.specificationId, {
    sourceSpecificationId: props.specificationId,
    code: values.code,
    label: values.label,
    description: values.description,
    modelId: values.model.id,
    crs: values.crs ? values.crs.name : 'Lambert72', // TODO: fix default crs
    schema: values.schema,
    dataConnectionId: values.dataConnection.id
  });
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

defineExpose({ onSubmit });
</script>
